/**
 * PLACEHOLDER WORDING — editorial to replace; ids are permanent.
 *
 * The feed's "Ask <handle>" chips (community feed plan §2 "Chip bank", §5): hand-written owner
 * questions per community category. A tap on a chip puts its `text` in the Ask field; posted
 * unedited, it is a question on the product remembered by DealHunt under its `id` (contract
 * #205), so the chip later shows as "… · asked" / "… · answered" on every review of that product
 * (#206).
 *
 * How to edit (an editor can change this file safely):
 * - `text` may be reworded at any time. Keep it a question an owner can answer from use, 3–150
 *   characters, one line. No price question, nothing generated.
 * - `id` is PERMANENT: never rename it, never reuse it for another question — DealHunt stores it
 *   on every question asked from the chip, and a renamed id silently loses that history. To retire
 *   a chip, delete its row (its id then stays unused forever); to add one, give it a new id.
 *   Ids are `<category key>.<chip key>`, lowercase letters, digits and `_` only (no `-`: DealHunt
 *   refuses it), at most 64 characters.
 * - `read` (optional) is the owner-review read the chip asks about. When the review already says
 *   that read came out better or worse than expected, the chip is not offered on it. It must be
 *   one of `ASK_READ_KEYS` (DealHunt's read ids, `categoryQuestionSets.ts`).
 * - The order is the order chips are offered in: a review shows the first three that apply.
 * - 6–8 chips per category. `src/tests/community-ask-chips.spec.ts` checks all of the above.
 *
 * TypeScript rather than JSON so this header and the per-row types travel with the data: a
 * missing `text` or a misspelt `read` fails the build, not a reader's tap.
 */

/** DealHunt's read ids across the question sets the four categories resolve to. */
export const ASK_READ_KEYS = [
  'comfort',
  'anc',
  'battery',
  'mic',
  'build',
  'positional',
  'connection',
  'loudness',
  'setup',
  'isolation',
  'cable',
] as const
export type AskReadKey = (typeof ASK_READ_KEYS)[number]

export interface AskChip {
  /** Permanent: `<category key>.<chip key>` — see the header. */
  id: string
  /** The question as the reader taps it and the owner receives it. */
  text: string
  /** The read it asks about, when one applies. */
  read?: AskReadKey
}

export interface AskChipCategory {
  /** The id prefix. Permanent like the ids. */
  key: string
  /** Community category paths (`/community/<path>`) the bank covers, subcategories included. The
   *  most specific match wins: gaming headsets sit under headphones. */
  paths: string[]
  chips: AskChip[]
}

export const ASK_CHIP_BANK: ReadonlyArray<AskChipCategory> = [
  {
    key: 'over_ears',
    paths: ['headphones'],
    chips: [
      { id: 'over_ears.still_using', text: 'Still using them every day?' },
      { id: 'over_ears.glasses', text: 'Comfortable with glasses on?', read: 'comfort' },
      { id: 'over_ears.battery_now', text: 'How long does the battery last now?', read: 'battery' },
      {
        id: 'over_ears.anc_travel',
        text: 'How well does the noise cancelling handle a plane or a train?',
        read: 'anc',
      },
      { id: 'over_ears.mic_calls', text: 'How do you sound to people on calls?', read: 'mic' },
      {
        id: 'over_ears.pads_wear',
        text: 'How have the ear pads and headband held up?',
        read: 'build',
      },
      { id: 'over_ears.warm', text: 'Do they get warm after an hour or two?' },
    ],
  },
  {
    key: 'earbuds',
    paths: ['earbuds'],
    chips: [
      { id: 'earbuds.still_using', text: 'Still using them every day?' },
      {
        id: 'earbuds.stay_in',
        text: 'Do they stay in during a run or a workout?',
        read: 'comfort',
      },
      { id: 'earbuds.battery_now', text: 'How long does the battery last now?', read: 'battery' },
      {
        id: 'earbuds.anc_commute',
        text: 'How well does the noise cancelling handle a commute?',
        read: 'anc',
      },
      { id: 'earbuds.mic_outside', text: 'How do you sound on calls outdoors?', read: 'mic' },
      { id: 'earbuds.case_wear', text: 'How have the case and its lid held up?', read: 'build' },
      { id: 'earbuds.dropouts', text: 'Do they ever drop out or fall out of sync?' },
    ],
  },
  {
    key: 'speakers',
    paths: ['speakers'],
    chips: [
      { id: 'speakers.fill_room', text: 'Is it loud enough to fill a room?', read: 'loudness' },
      { id: 'speakers.distortion', text: 'Does it distort when you turn it up?', read: 'loudness' },
      {
        id: 'speakers.battery_real',
        text: 'How long does the battery last at a party?',
        read: 'battery',
      },
      {
        id: 'speakers.connection',
        text: 'Does the Bluetooth connection drop?',
        read: 'connection',
      },
      { id: 'speakers.setup', text: 'Was it easy to set up and place?', read: 'setup' },
      { id: 'speakers.held_up', text: 'How has it held up after months?', read: 'build' },
      { id: 'speakers.bass_low', text: 'How is the bass at low volume?' },
    ],
  },
  {
    key: 'gaming',
    paths: ['headphones/gaming-headsets'],
    chips: [
      { id: 'gaming.long_sessions', text: 'Comfortable over a long session?', read: 'comfort' },
      { id: 'gaming.mic_teammates', text: 'What do teammates say about the mic?', read: 'mic' },
      { id: 'gaming.footsteps', text: 'Can you place footsteps with it?', read: 'positional' },
      { id: 'gaming.wireless_lag', text: 'Any lag or drop-outs on wireless?', read: 'connection' },
      { id: 'gaming.console', text: 'How well does it work with your console?' },
      { id: 'gaming.headband', text: 'How has the headband held up?', read: 'build' },
      { id: 'gaming.glasses', text: 'Comfortable with glasses on?', read: 'comfort' },
    ],
  },
]
